import React, { useEffect } from 'react';
import { colors, borders, fonts } from './tokens';

interface DrillDrawerProps {
    open: boolean;
    onClose: () => void;
    title: string;
    /** Eyebrow line, e.g. "DIMENSION · 19% · GROWTH POINT". Score-color on the user's side. */
    eyebrow?: string;
    /** Accent color for eyebrow, hero question, list dashes, insight border. */
    color?: string;
    children: React.ReactNode;
}

/**
 * Editorial Calm drill-down drawer.
 *
 * Slide-up panel from the bottom on mobile, centered card on desktop. Uses inline
 * styles only (no SCSS dependency) so it composes cleanly with the rest of the
 * editorial dashboard.
 */
const DrillDrawer: React.FC<DrillDrawerProps> = ({ open, onClose, title, eyebrow, color = '#3B82F6', children }) => {
    useEffect(() => {
        if (!open) return undefined;

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            role="dialog"
            aria-modal="true"
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 100,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                fontFamily: fonts.display,
                fontFeatureSettings: '"ss01","cv11","tnum"',
            }}
        >
            <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(5, 8, 14, 0.6)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    border: 0,
                    cursor: 'pointer',
                }}
            />
            <div
                style={{
                    position: 'relative',
                    // Mobile: fills viewport width. Desktop: caps near 880px so
                    // the editorial layout has breathing room without spanning
                    // edge-to-edge on ultra-wide displays.
                    width: 'min(880px, 100vw)',
                    maxHeight: '92vh',
                    background: '#0a0a0a',
                    border: borders.hairline,
                    borderBottom: 'none',
                    borderRadius: '20px 20px 0 0',
                    boxShadow: '0 -20px 60px rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    color: colors.text,
                }}
            >
                <div
                    style={{
                        width: 48,
                        height: 4,
                        background: 'rgba(255, 255, 255, 0.18)',
                        borderRadius: 999,
                        margin: '12px auto 0',
                        flexShrink: 0,
                    }}
                />

                <div
                    style={{
                        padding: '20px clamp(20px, 4vw, 36px) 18px',
                        borderBottom: borders.hairline,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 14,
                        flexShrink: 0,
                    }}
                >
                    <div style={{ minWidth: 0 }}>
                        {eyebrow && (
                            <div
                                style={{
                                    fontSize: 10,
                                    letterSpacing: '0.22em',
                                    color,
                                    fontFamily: fonts.mono,
                                    fontWeight: 500,
                                    marginBottom: 8,
                                }}
                            >
                                {eyebrow}
                            </div>
                        )}
                        <h2
                            style={{
                                margin: 0,
                                fontSize: 'clamp(26px, 3.5vw, 36px)',
                                fontWeight: 400,
                                color: colors.textPrimary,
                                letterSpacing: '-0.02em',
                                lineHeight: 1.1,
                            }}
                        >
                            {title}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.04)',
                            color: colors.muted,
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            flexShrink: 0,
                            fontFamily: 'inherit',
                        }}
                    >
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>

                <div
                    style={{
                        padding: '24px clamp(20px, 4vw, 36px) 40px',
                        overflowY: 'auto',
                        flex: 1,
                    }}
                >
                    {children}
                </div>
            </div>
        </div>
    );
};

// ---------- Reusable detail building blocks ----------

interface DetailHeroProps {
    question?: string;
    description?: string;
}

export const DetailHero: React.FC<DetailHeroProps> = ({ question, description }) => (
    <div style={{ paddingBottom: 18, marginBottom: 18, borderBottom: borders.hair2 }}>
        {question && (
            <h3
                style={{
                    margin: '0 0 8px',
                    fontSize: 17,
                    fontWeight: 500,
                    color: colors.textPrimary,
                    lineHeight: 1.4,
                    letterSpacing: '-0.01em',
                }}
            >
                {question}
            </h3>
        )}
        {description && (
            <p style={{ margin: 0, fontSize: 14, color: colors.muted, lineHeight: 1.6 }}>{description}</p>
        )}
    </div>
);

interface DetailSectionProps {
    label: string;
    children: React.ReactNode;
}

export const DetailSection: React.FC<DetailSectionProps> = ({ label, children }) => (
    <div style={{ marginBottom: 28 }}>
        <div
            style={{
                fontSize: 10,
                letterSpacing: '0.22em',
                color: colors.muted,
                fontFamily: fonts.mono,
                fontWeight: 500,
                marginBottom: 14,
            }}
        >
            {label}
        </div>
        {children}
    </div>
);

interface DetailMeasureListProps {
    items: string[];
    accentColor: string;
}

/** Bulleted list with a short accent dash and hairline separators. */
export const DetailMeasureList: React.FC<DetailMeasureListProps> = ({ items, accentColor }) => (
    <ul className="dash-edit__measure-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {items.map((m, i) => (
            <li
                key={m}
                style={{
                    position: 'relative',
                    padding: '12px 0 12px 24px',
                    fontSize: 14,
                    color: colors.text,
                    lineHeight: 1.5,
                    borderBottom: i === items.length - 1 ? 'none' : borders.hair2,
                }}
            >
                <span
                    style={{
                        position: 'absolute',
                        top: 22,
                        left: 0,
                        width: 14,
                        height: 1,
                        background: accentColor,
                    }}
                />
                {m}
            </li>
        ))}
    </ul>
);

interface DetailInsightCardProps {
    body: string;
    cite?: string;
    sourceUrl?: string;
    accentColor: string;
    /** Italic-rendered when true (for priority messages, kept distinct from regular citations). */
    italic?: boolean;
}

/** Body card with a left accent border. Used for "Where you stand" and research insights. */
export const DetailInsightCard: React.FC<DetailInsightCardProps> = ({ body, cite, sourceUrl, accentColor, italic }) => (
    <div
        style={{
            background: 'rgba(255, 255, 255, 0.02)',
            borderLeft: `2px solid ${accentColor}`,
            borderRadius: '0 8px 8px 0',
            padding: '14px 18px',
            marginBottom: 12,
        }}
    >
        <p
            style={{
                margin: 0,
                fontSize: 14,
                color: colors.textPrimary,
                lineHeight: 1.55,
            }}
        >
            {body}
        </p>
        {cite && sourceUrl && !italic && (
            <a
                href={sourceUrl}
                target="_blank"
                rel="noreferrer"
                style={{
                    display: 'inline-block',
                    marginTop: 10,
                    fontSize: 12,
                    color: colors.muted,
                    fontFamily: fonts.mono,
                    letterSpacing: '0.01em',
                    lineHeight: 1.5,
                    textDecorationColor: accentColor,
                    textUnderlineOffset: 3,
                }}
            >
                {cite} ↗
            </a>
        )}
        {cite && (!sourceUrl || italic) && (
            <div
                style={{
                    marginTop: 10,
                    fontSize: 12,
                    color: italic ? accentColor : colors.muted,
                    fontFamily: italic ? fonts.display : fonts.mono,
                    fontStyle: italic ? 'italic' : 'normal',
                    letterSpacing: italic ? 0 : '0.01em',
                    lineHeight: 1.5,
                }}
            >
                {cite}
            </div>
        )}
    </div>
);

export default DrillDrawer;
